/**
 * SuggestionPanel Component - SPEC-019 Task 5.5, 10.1
 *
 * Main panel for displaying AI suggestions with Backlinks and Tags sections.
 * Supports Popover (desktop) and Drawer (mobile) display modes.
 * Includes full keyboard navigation support (SPEC-019 Requirements 9.1-9.7).
 *
 * @module features/ai-button/components/SuggestionPanel
 * @see SPEC-019 Requirements 2.1, 2.2, 2.5, 2.6, 2.7, 2.8, 2.9, 8.3, 9.1-9.7
 */

import { memo, useId, useRef, useCallback, useState, useEffect } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { BacklinkSuggestion, TagSuggestion, FeedbackAction } from '@linkode/shared';
import { cn } from '../../../lib/cn';
import { useTypedTranslation } from '../../../i18n';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { SuggestionCard } from './SuggestionCard';
import { DegradedStateBanner, type DegradeReason, type SuggestHint } from './DegradedStateBanner';

// ============================================================================
// CVA Variants
// ============================================================================

/**
 * SuggestionPanel variants using CVA
 */
export const suggestionPanelVariants = cva(
  // Base styles
  ['bg-white border shadow-lg', 'flex flex-col', 'overflow-hidden'],
  {
    variants: {
      displayMode: {
        popover: ['rounded-lg', 'w-96 max-h-[32rem]', 'absolute z-50'],
        drawer: ['rounded-t-2xl', 'w-full max-h-[80vh]', 'fixed bottom-0 left-0 right-0 z-50'],
      },
    },
    defaultVariants: {
      displayMode: 'popover',
    },
  }
);

export type SuggestionPanelVariants = VariantProps<typeof suggestionPanelVariants>;

// ============================================================================
// Types
// ============================================================================

export interface SuggestionPanelProps extends SuggestionPanelVariants {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Display mode */
  displayMode: 'popover' | 'drawer';
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error?: string;
  /** Degraded state */
  degraded?: boolean;
  /** Degradation reason */
  degradeReason?: DegradeReason;
  /** Hint for improvement */
  hint?: SuggestHint;
  /** Backlink suggestions */
  backlinks: BacklinkSuggestion[];
  /** Tag suggestions */
  tags: TagSuggestion[];
  /** Selected backlink IDs */
  selectedBacklinks: Set<string>;
  /** Selected tag strings */
  selectedTags: Set<string>;
  /** Feedback state per candidate */
  feedbackState?: Map<string, FeedbackAction>;
  /** Toggle backlink selection */
  onToggleBacklink: (docId: string) => void;
  /** Toggle tag selection */
  onToggleTag: (tag: string) => void;
  /** Select all backlinks */
  onSelectAllBacklinks: () => void;
  /** Select all tags */
  onSelectAllTags: () => void;
  /** Clear backlink selections */
  onClearBacklinks: () => void;
  /** Clear tag selections */
  onClearTags: () => void;
  /** Apply selected suggestions */
  onApply: () => void;
  /** Close panel */
  onClose: () => void;
  /** Submit feedback */
  onFeedback: (candidateId: string, kind: 'backlink' | 'tag', action: FeedbackAction) => void;
  /** Retry fetch */
  onRetry?: () => void;
  /** Ref to trigger element for focus restoration */
  triggerRef?: React.RefObject<HTMLElement | null>;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Loading skeleton */
function LoadingSkeleton() {
  return (
    <div data-testid="loading-skeleton" className="p-4 space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
          <div className="h-3 bg-gray-200 rounded w-full mb-1" />
          <div className="h-2 bg-gray-200 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

/** Empty state */
function EmptyState({ message }: { message: string }) {
  return (
    <div data-testid="empty-state" className="p-8 text-center text-gray-500">
      <svg
        className="w-12 h-12 mx-auto mb-3 text-gray-300"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
      <p>{message}</p>
    </div>
  );
}

/** Error state */
function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTypedTranslation();
  return (
    <div data-testid="error-state" className="p-8 text-center">
      <svg
        className="w-12 h-12 mx-auto mb-3 text-red-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <p className="text-red-600 mb-3">{message}</p>
      {onRetry && (
        <button
          type="button"
          data-testid="retry-button"
          className="px-4 py-3 min-h-11 touch-target text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 motion-reduce:transition-none"
          onClick={onRetry}
        >
          {t('ai.panel.retry')}
        </button>
      )}
    </div>
  );
}

/** Section header with select all / clear buttons */
function SectionHeader({
  title,
  count,
  selectedCount,
  onSelectAll,
  onClear,
}: {
  title: string;
  count: number;
  selectedCount: number;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const { t } = useTypedTranslation();
  return (
    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
      <h3 className="text-sm font-medium text-gray-700">
        {title} ({selectedCount}/{count})
      </h3>
      <div className="flex gap-2">
        <button
          type="button"
          className="text-xs text-blue-600 hover:text-blue-800 py-2 px-2 min-h-11 touch-target motion-reduce:transition-none"
          onClick={onSelectAll}
          disabled={count === 0}
        >
          {t('ai.panel.selectAll')}
        </button>
        <button
          type="button"
          className="text-xs text-gray-500 hover:text-gray-700 py-2 px-2 min-h-11 touch-target motion-reduce:transition-none"
          onClick={onClear}
          disabled={selectedCount === 0}
        >
          {t('ai.panel.clearSelection')}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

/**
 * SuggestionPanel Component
 *
 * Main panel for AI suggestions with:
 * - Popover/Drawer display modes
 * - Loading/Empty/Error states
 * - Degraded state banner
 * - Backlinks and Tags sections
 * - Select All / Clear actions
 * - Apply Selected button
 * - Focus trap for accessibility
 * - Full keyboard navigation (Arrow keys, Space, Escape, Home, End)
 *
 * @see SPEC-019 Requirements 2.1-2.9, 8.3, 9.1-9.7
 */
export const SuggestionPanel = memo(function SuggestionPanel({
  isOpen,
  displayMode,
  isLoading,
  error,
  degraded,
  degradeReason,
  hint,
  backlinks,
  tags,
  selectedBacklinks,
  selectedTags,
  feedbackState,
  onToggleBacklink,
  onToggleTag,
  onSelectAllBacklinks,
  onSelectAllTags,
  onClearBacklinks,
  onClearTags,
  onApply,
  onClose,
  onFeedback,
  onRetry,
  triggerRef,
}: SuggestionPanelProps) {
  const { t } = useTypedTranslation();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [focusedCardIndex, setFocusedCardIndex] = useState(-1);

  // Build flat list of all suggestion items for keyboard navigation
  const allItems = [
    ...backlinks.map((bl) => ({ type: 'backlink' as const, id: bl.docId })),
    ...tags.map((tg) => ({ type: 'tag' as const, id: tg.tag })),
  ];

  // Focus trap for accessibility
  useFocusTrap({
    enabled: isOpen,
    containerRef: panelRef,
    returnFocusRef: triggerRef,
  });

  // Focus the card at the current index
  const focusCard = useCallback(
    (index: number) => {
      if (!panelRef.current || index < 0 || index >= allItems.length) return;

      const cards = panelRef.current.querySelectorAll<HTMLElement>(
        '[data-testid^="suggestion-card-"]'
      );
      if (cards[index]) {
        cards[index].focus();
        setFocusedCardIndex(index);
      }
    },
    [allItems.length]
  );

  // Keyboard navigation handler (Requirements 9.1-9.7)
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const totalCards = allItems.length;
      if (totalCards === 0) return;

      switch (event.key) {
        // Requirement 9.1: Escape closes panel
        case 'Escape':
          event.preventDefault();
          onClose();
          break;

        // Requirement 9.2: Arrow Down navigates to next card
        case 'ArrowDown': {
          event.preventDefault();
          const nextIndex = focusedCardIndex < 0 ? 0 : (focusedCardIndex + 1) % totalCards;
          focusCard(nextIndex);
          break;
        }

        // Requirement 9.3: Arrow Up navigates to previous card
        case 'ArrowUp': {
          event.preventDefault();
          const prevIndex = focusedCardIndex <= 0 ? totalCards - 1 : focusedCardIndex - 1;
          focusCard(prevIndex);
          break;
        }

        // Requirement 9.4: Space toggles checkbox
        case ' ': {
          if (focusedCardIndex >= 0 && focusedCardIndex < totalCards) {
            event.preventDefault();
            const item = allItems[focusedCardIndex];
            if (item) {
              if (item.type === 'backlink') {
                onToggleBacklink(item.id);
              } else {
                onToggleTag(item.id);
              }
            }
          }
          break;
        }

        // Requirement 9.5: Enter activates focused element (handled natively for buttons)
        case 'Enter': {
          // Let native button behavior handle this
          break;
        }

        // Requirement 9.6: Home moves to first card
        case 'Home': {
          event.preventDefault();
          focusCard(0);
          break;
        }

        // Requirement 9.7: End moves to last card
        case 'End': {
          event.preventDefault();
          focusCard(totalCards - 1);
          break;
        }
      }
    },
    [allItems, focusedCardIndex, focusCard, onClose, onToggleBacklink, onToggleTag]
  );

  // Track focus changes on cards
  const handleCardFocus = useCallback((index: number) => {
    setFocusedCardIndex(index);
  }, []);

  // Reset focus index when panel closes
  useEffect(() => {
    if (!isOpen) {
      setFocusedCardIndex(-1);
    }
  }, [isOpen]);

  // Don't render if not open
  if (!isOpen) return null;

  const totalSelected = selectedBacklinks.size + selectedTags.size;
  const hasContent = backlinks.length > 0 || tags.length > 0;

  return (
    <div
      ref={panelRef}
      className={cn(suggestionPanelVariants({ displayMode }))}
      data-testid="suggestion-panel"
      data-mode={displayMode}
      role="dialog"
      aria-labelledby={titleId}
      aria-modal="true"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h2 id={titleId} className="text-base font-semibold text-gray-900">
          {t('ai.panel.title')}
        </h2>
        <button
          type="button"
          className="p-2 min-h-11 min-w-11 touch-target text-gray-400 hover:text-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 motion-reduce:transition-none"
          onClick={onClose}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Degraded Banner */}
      {degraded && degradeReason && (
        <div className="px-4 pt-3">
          <DegradedStateBanner
            degradeReason={degradeReason}
            hint={hint}
            dismissible={false}
            onDismiss={() => {}}
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && <LoadingSkeleton />}

        {!isLoading && error && <ErrorState message={error} onRetry={onRetry} />}

        {!isLoading && !error && !hasContent && <EmptyState message={t('ai.panel.empty')} />}

        {!isLoading && !error && hasContent && (
          <>
            {/* Backlinks Section */}
            {backlinks.length > 0 && (
              <div>
                <SectionHeader
                  title={t('ai.panel.backlinks')}
                  count={backlinks.length}
                  selectedCount={selectedBacklinks.size}
                  onSelectAll={onSelectAllBacklinks}
                  onClear={onClearBacklinks}
                />
                <div className="p-2 space-y-2">
                  {backlinks.map((bl, index) => (
                    <SuggestionCard
                      key={bl.docId}
                      candidateId={bl.docId}
                      kind="backlink"
                      title={bl.title}
                      reason={bl.reason}
                      score={bl.score}
                      scoreBreakdown={bl.scoreBreakdown}
                      isSelected={selectedBacklinks.has(bl.docId)}
                      feedback={feedbackState?.get(bl.docId)}
                      onToggleSelect={() => onToggleBacklink(bl.docId)}
                      onFeedback={(action) => onFeedback(bl.docId, 'backlink', action)}
                      tabIndex={0}
                      onFocus={() => handleCardFocus(index)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Tags Section */}
            {tags.length > 0 && (
              <div>
                <SectionHeader
                  title={t('ai.panel.tags')}
                  count={tags.length}
                  selectedCount={selectedTags.size}
                  onSelectAll={onSelectAllTags}
                  onClear={onClearTags}
                />
                <div className="p-2 space-y-2">
                  {tags.map((tg, index) => (
                    <SuggestionCard
                      key={tg.tag}
                      candidateId={tg.tag}
                      kind="tag"
                      title={tg.tag}
                      reason={tg.reason}
                      score={tg.score}
                      scoreBreakdown={tg.scoreBreakdown}
                      isSelected={selectedTags.has(tg.tag)}
                      feedback={feedbackState?.get(tg.tag)}
                      onToggleSelect={() => onToggleTag(tg.tag)}
                      onFeedback={(action) => onFeedback(tg.tag, 'tag', action)}
                      tabIndex={0}
                      onFocus={() => handleCardFocus(backlinks.length + index)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer with Apply Button */}
      <div className="px-4 py-3 border-t bg-gray-50">
        <button
          type="button"
          data-testid="apply-button"
          className={cn(
            'w-full py-3 px-4 rounded-md font-medium text-sm',
            'min-h-11 touch-target',
            'transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'motion-reduce:transition-none',
            totalSelected > 0
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          )}
          disabled={totalSelected === 0}
          onClick={onApply}
        >
          {t('ai.panel.applySelected')} {totalSelected > 0 && `(${totalSelected})`}
        </button>
      </div>
    </div>
  );
});
